import qs from 'qs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { AnyFilter } from '../../shared/organizationPackages/filters';
import type { SortOption } from '../../shared/organizationPackages/sortOptions';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../components/generic/Pagination/consts';
import { useLocation, useRouter } from '../contexts/RouterContext';
import { flattenFilters, parseQueryParams } from '../helpers/packageInventory';
import type { PackageInventoryURLState } from '../helpers/packageInventory';
import TeamsStore from '../redux/teams';

import useDebouncedCallback from './useDebouncedCallback';

const URL_UPDATE_DEBOUNCE_MS = 750;

const DEFAULT_FILTERS: AnyFilter = {};

type FilterPatch = {
  [Key in keyof AnyFilter]?: AnyFilter[Key] | null;
};

const cleanFilters = (filters: FilterPatch): AnyFilter => Object.fromEntries(
  Object.entries(filters).filter(([, value]) => {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    return !Array.isArray(value) || value.length > 0;
  }),
);

const getSortOrDefault = (sort: SortOption | null | undefined, packageName: string | undefined): SortOption => {
  if (sort === 'match' && !packageName) {
    return 'alphabetical';
  }
  if (sort === 'alphabetical' && packageName) {
    return 'match';
  }
  return sort ?? (packageName ? 'match' : 'alphabetical');
};

export const parseSearchParams = (search: string): PackageInventoryURLState => {
  const parsed = parseQueryParams(qs.parse(search, { ignoreQueryPrefix: true }));
  const filter = parsed.filter ?? DEFAULT_FILTERS;
  return {
    page: parsed.page ?? DEFAULT_PAGE,
    count: parsed.count ?? DEFAULT_PAGE_SIZE,
    sort: getSortOrDefault(parsed.sort, filter.packageName),
    filter,
  };
};

const usePackageInventorySearchParams = () => {
  const location = useLocation();
  const router = useRouter();
  const teamIds = useSelector(TeamsStore.selectors.getTeamSelection);
  const parsedState = useMemo(() => parseSearchParams(location.search ?? ''), [location.search]);
  const pendingStateRef = useRef(parsedState);
  // Filters set since the URL last changed. Ignored once the URL changes, since it then holds the newest state.
  const [localFilters, setLocalFilters] = useState<{
    urlState: PackageInventoryURLState;
    filter: AnyFilter;
  } | null>(null);

  const replaceSearchParams = useCallback((state: PackageInventoryURLState) => {
    router.replace({
      pathname: '/packages',
      search: `?${qs.stringify(flattenFilters(state))}`,
    });
  }, [router]);

  const replaceSearchParamsDebounced = useDebouncedCallback(() => {
    replaceSearchParams(pendingStateRef.current);
  }, URL_UPDATE_DEBOUNCE_MS);

  useEffect(() => {
    pendingStateRef.current = parsedState;
    replaceSearchParamsDebounced.cancel();
  }, [parsedState, replaceSearchParamsDebounced]);

  const buildFilteredState = useCallback((partialFilters: FilterPatch) => {
    const currentState = pendingStateRef.current;
    const filter = cleanFilters({
      ...currentState.filter,
      ...partialFilters,
    });
    return {
      ...currentState,
      page: DEFAULT_PAGE,
      sort: getSortOrDefault(currentState.sort, filter.packageName),
      filter,
    };
  }, []);

  const setFilters = useCallback((partialFilters: FilterPatch) => {
    const nextState = buildFilteredState(partialFilters);
    pendingStateRef.current = nextState;
    setLocalFilters({ urlState: parsedState, filter: nextState.filter });
    replaceSearchParams(nextState);
  }, [buildFilteredState, parsedState, replaceSearchParams]);

  const setFiltersDebounced = useCallback((partialFilters: FilterPatch) => {
    const nextState = buildFilteredState(partialFilters);
    pendingStateRef.current = nextState;
    setLocalFilters({ urlState: parsedState, filter: nextState.filter });
    replaceSearchParamsDebounced();
  }, [buildFilteredState, parsedState, replaceSearchParamsDebounced]);

  const setPage = useCallback((page: number) => {
    const nextState = { ...pendingStateRef.current, page };
    pendingStateRef.current = nextState;
    replaceSearchParams(nextState);
  }, [replaceSearchParams]);

  const setCount = useCallback((count: number) => {
    const nextState = { ...pendingStateRef.current, page: DEFAULT_PAGE, count };
    pendingStateRef.current = nextState;
    replaceSearchParams(nextState);
  }, [replaceSearchParams]);

  const setSort = useCallback((sort: SortOption) => {
    const nextState = {
      ...pendingStateRef.current,
      sort: getSortOrDefault(sort, pendingStateRef.current.filter.packageName),
    };
    pendingStateRef.current = nextState;
    replaceSearchParams(nextState);
  }, [replaceSearchParams]);

  const resetFilters = useCallback(() => {
    const nextState: PackageInventoryURLState = {
      ...pendingStateRef.current,
      page: DEFAULT_PAGE,
      sort: getSortOrDefault(pendingStateRef.current.sort, undefined),
      filter: DEFAULT_FILTERS,
    };
    pendingStateRef.current = nextState;
    replaceSearchParams(nextState);
  }, [replaceSearchParams]);

  const params = useMemo(() => ({
    ...flattenFilters(parsedState),
    teamIds,
  }), [parsedState, teamIds]);
  const queryString = useMemo(() => qs.stringify(flattenFilters(parsedState)), [parsedState]);
  const filters = localFilters?.urlState === parsedState ? localFilters.filter : parsedState.filter;
  const versionQueryString = useMemo(() => qs.stringify({
    ...flattenFilters(parsedState),
    page: DEFAULT_PAGE,
  }), [parsedState]);

  return {
    filters,
    page: parsedState.page,
    count: parsedState.count,
    sort: getSortOrDefault(parsedState.sort, parsedState.filter.packageName),
    params,
    queryString,
    versionQueryString,
    setFilters,
    setFiltersDebounced,
    setPage,
    setCount,
    setSort,
    resetFilters,
  };
};

export type PackageInventorySearchParams = ReturnType<typeof usePackageInventorySearchParams>;

export default usePackageInventorySearchParams;
